import type { ReactElement } from 'react';
import { IconHistory } from '@tabler/icons-react';

import { V4 } from '../../../tokens';
import { Button } from '../../../Button/Button';
import type { PendingJob } from '../utils/pendingJob';

export interface RecoveryBannerProps {
  pending: PendingJob;
  onRecover: () => void;
  onDiscard: () => void;
}

/**
 * Banner que aparece al volver a la pantalla de upload cuando hay un job
 * persistido de una sesión anterior (la página se recargó durante el análisis).
 * Ofrece recuperar el resultado por jobId — sin re-subir ni re-pagar el LLM —
 * o descartarlo y empezar de cero.
 *
 * @param {RecoveryBannerProps} props Pending job + callbacks de recuperar/descartar.
 * @returns {ReactElement} El banner de recuperación.
 */
export const RecoveryBanner = ({ pending, onRecover, onDiscard }: RecoveryBannerProps): ReactElement => {
  const minutesAgo = Math.max(1, Math.round((Date.now() - pending.startedAt) / 60_000));
  const fileCount = pending.files.length;
  const plural = fileCount === 1 ? '' : 's';

  return (
    <div
      data-testid="smart-menu-recovery-banner"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: V4.grayUltraLight,
        border: `1.5px solid ${V4.secondary}`,
        borderRadius: 8,
      }}
    >
      <IconHistory size={20} color={V4.secondary} aria-hidden="true" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 200, fontSize: 13, color: V4.primary, lineHeight: 1.5 }}>
        <strong style={{ display: 'block' }}>Tenías un menú procesándose</strong>
        {fileCount} archivo{plural} enviado{plural} hace ~{minutesAgo} min. Puedes recuperar el resultado sin volver a
        subirlo{plural === 's' ? 's' : ''}.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button data-testid="smart-menu-recovery-recover" variant="primary" size="sm" onClick={onRecover}>
          Recuperar
        </Button>
        <Button data-testid="smart-menu-recovery-discard" variant="ghost" size="sm" onClick={onDiscard}>
          Empezar de nuevo
        </Button>
      </div>
    </div>
  );
};
